import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, LogOut, Music, Menu, X, Settings, BarChart3 } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

export const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const { itemCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/music', label: 'Music' },
    { path: '/videos', label: 'Videos' },
    { path: '/live', label: 'Live' },
    { path: '/presaves', label: 'Pre-save' },
    { path: '/merch', label: 'Merch' },
    { path: '/marketplace', label: 'Marketplace' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="container">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
              <Music className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight hidden sm:block" style={{ fontFamily: 'Syne, sans-serif' }}>
              @DaRealQJones
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link 
                  to="/cart" 
                  className="relative p-2 hover:bg-white/5 rounded-lg"
                  data-testid="cart-button"
                >
                  <ShoppingCart className="w-5 h-5 text-gray-300" />
                  {itemCount > 0 && (
                    <span className="cart-badge" data-testid="cart-count">{itemCount}</span>
                  )}
                </Link>
                {isAdmin && (
                  <>
                    <Link
                      to="/admin"
                      className="p-2 hover:bg-white/5 rounded-lg"
                      data-testid="admin-link"
                      title="Admin Panel"
                    >
                      <Settings className="w-5 h-5 text-gray-300" />
                    </Link>
                    <Link
                      to="/analytics"
                      className="p-2 hover:bg-white/5 rounded-lg"
                      data-testid="analytics-link"
                      title="Analytics"
                    >
                      <BarChart3 className="w-5 h-5 text-gray-300" />
                    </Link>
                  </>
                )}
                <Link
                  to="/dashboard"
                  className="hidden sm:flex items-center gap-2 px-3 py-2 hover:bg-white/5 rounded-lg"
                  data-testid="dashboard-link"
                >
                  <User className="w-5 h-5 text-gray-300" />
                  <span className="text-sm text-gray-300 subheading">{user?.name?.split(' ')[0]}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="p-2 hover:bg-white/5 rounded-lg"
                  data-testid="logout-button"
                >
                  <LogOut className="w-5 h-5 text-gray-300" />
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="btn-primary text-sm py-2 px-4"
                data-testid="login-button"
              >
                Sign In
              </Link>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-white/5 rounded-lg"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden glass border-t border-white/10">
          <div className="container py-4 flex flex-col gap-2">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg ${
                  location.pathname === link.path 
                    ? 'bg-purple-500/20 text-white' 
                    : 'text-gray-300 hover:bg-white/5'
                }`}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
            {isAuthenticated && (
              <Link
                to="/dashboard"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 rounded-lg text-gray-300 hover:bg-white/5 flex items-center gap-2"
                data-testid="mobile-dashboard-link"
              >
                <User className="w-4 h-4" />
                Dashboard
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
